/** @jsxImportSource @emotion/react */
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { initializeApp } from "firebase/app";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useRef, useState } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { css, keyframes } from "@emotion/react";
import {
  Button,
  Column,
  Input,
  Separator,
  Text,
  TEXT_VARIANTS,
} from "../components";
import { COLORS } from "../shared/theme";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as Google } from "../icons/GOOGLE.svg";
import axios from "axios";
import validator from "validator";
import { sequence, run, unit } from "tween-fn";

const fadeIn = keyframes`
  from: {
    opacity: 0;
    transform: scale(0.98);
  }
  to: {
    opacity: 1;
    transform: scale(1);
  }
`;

const app = initializeApp({
  apiKey: "AIzaSyD3eQCNI6oAkbOmc8SMiHfFNtsnnjr0qmQ",
  authDomain: "sublime-94c68.firebaseapp.com",
  projectId: "sublime-94c68",
  storageBucket: "sublime-94c68.appspot.com",
  messagingSenderId: "592548959549",
  appId: "1:592548959549:web:0ad541e27daa21cd82e154",
  measurementId: "G-S85PS98RP9",
});

type TFormValues = {
  email: string;
};

const SignIn: React.FC = () => {
  const [requestedLink, setRequestedLink] = useState(false);
  const navigate = useNavigate();
  const { control, handleSubmit, reset, formState, watch } =
    useForm<TFormValues>({
      defaultValues: {
        email: "",
      },
    });
  const email = watch("email");
  const formWrapper = useRef<HTMLDivElement | null>(null);
  const requestedLinkNotice = useRef<HTMLDivElement | null>(null);

  const authenticate = useCallback(async () => {
    const auth = getAuth(app);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      navigate("/");
    } catch (e) {
      console.error(e);
    }
  }, [navigate]);

  const requestLink = useCallback(() => {
    const seq = sequence([
      unit({
        duration: 100,
        change: (y) => {
          (formWrapper.current as HTMLDivElement).style.opacity = (
            1 - y
          ).toString();
        },
        complete: () => setRequestedLink(true),
      }),
      unit({
        delay: 100,
        duration: 100,
        change: (y) => {
          (requestedLinkNotice.current as HTMLDivElement).style.opacity = (
            0.8 +
            y * 0.2
          ).toString();
          (
            requestedLinkNotice.current as HTMLDivElement
          ).style.transform = `scale(${0.99 + y * 0.01})`;
        },
      }),
    ]);
    run(seq);
  }, []);

  const sendEmailLink = useCallback(
    async (values: TFormValues) => {
      try {
        axios.post("https://staging.sublime.app/api/v2/auth/sign-in", {
          email: values.email,
          continue_url: window.location.origin,
        });
        requestLink();
      } catch (e) {
        console.error(e);
      }
    },
    [requestLink]
  );

  return (
    <Column
      mainAxisAlignment="center"
      crossAxisAlignment="center"
      css={css`
        width: 100vw;
        height: 100vh;
      `}
    >
      <Column
        crossAxisAlignment="center"
        gap={30}
        css={css`
          width: 570px;
          max-width: calc(100% - 20px);
          height: 380px;
        `}
      >
        <Text
          variant={TEXT_VARIANTS.T30SOFT}
          css={css`
            color: ${COLORS.NUMBER};
          `}
        >
          {"SUBLIME"}
        </Text>
        {requestedLink ? (
          <Column
            ref={requestedLinkNotice}
            crossAxisAlignment="center"
            gap={30}
            css={css`
              animation: ${fadeIn} 200ms ease-in-out;
              width: 100%;
            `}
          >
            <Text variant={TEXT_VARIANTS.T30}>{"Check your email"}</Text>
            <Column crossAxisAlignment="center">
              <Text
                variant={TEXT_VARIANTS.T20}
                css={css`
                  text-align: center;
                `}
              >
                {"We’ve sent a temporary login link"}
              </Text>
              <Text
                variant={TEXT_VARIANTS.T20}
                css={css`
                  text-align: center;
                `}
              >
                {"Please check your inbox at "}
                <Text as="span" variant={TEXT_VARIANTS.T20BOLD}>
                  {email}
                </Text>
              </Text>
            </Column>
            <Text
              role="button"
              onClick={() => {
                setRequestedLink(false);
                reset();
              }}
              variant={TEXT_VARIANTS.T20}
              css={css`
                text-decoration: underline;
                color: ${COLORS.SILVER};

                &:hover {
                  cursor: pointer;
                }
              `}
            >
              {"Back to Login"}
            </Text>
          </Column>
        ) : (
          <Column
            ref={formWrapper}
            crossAxisAlignment="center"
            gap={30}
            css={css`
              animation: ${fadeIn} 200ms ease-in-out;
              width: 100%;
            `}
          >
            <Text variant={TEXT_VARIANTS.T30}>{"Login"}</Text>
            <form
              onSubmit={handleSubmit(sendEmailLink)}
              css={css`
                width: 100%;
              `}
            >
              <Column
                gap={10}
                css={css`
                  width: 100%;
                `}
              >
                <Column
                  gap={5}
                  css={css`
                    width: 100%;
                  `}
                >
                  <Controller
                    control={control}
                    name="email"
                    rules={{
                      required: "Please enter an email address to login",
                      validate: (value) => {
                        if (!validator.isEmail(value))
                          return "Please enter a valid email address";
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        type="text"
                        placeholder="Your email address"
                        css={css`
                          text-align: center;
                        `}
                        {...field}
                      />
                    )}
                  />
                  <ErrorMessage
                    errors={formState.errors}
                    name="email"
                    render={({ message }) => (
                      <Text
                        variant={TEXT_VARIANTS.T12}
                        css={css`
                          text-align: center;
                          color: ${COLORS.RED};
                        `}
                      >
                        {message}
                      </Text>
                    )}
                  />
                </Column>
                <Button
                  type="submit"
                  expand
                  variant={formState.isValid ? "caps-active" : "caps-disabled"}
                  size="big"
                  label="EMAIL ME A LOGIN LINK"
                  textVariant={TEXT_VARIANTS.T15CAPS}
                />
              </Column>
            </form>
            <Separator />
            <Button
              expand
              variant="caps"
              size="big"
              leadingElement={() => <Google />}
              label="CONTINUE WITH GOOGLE"
              textVariant={TEXT_VARIANTS.T15CAPS}
              onClick={authenticate}
            />
            <Text
              variant={TEXT_VARIANTS.T20}
              css={css`
                color: ${COLORS.SILVER};
              `}
            >
              {"Don't have an account? "}
              <Link to="/auth/sign-up">
                <Text
                  as="span"
                  variant={TEXT_VARIANTS.T20}
                  css={css`
                    color: ${COLORS.SILVER};
                    text-decoration: underline;
                  `}
                >
                  {"Join"}
                </Text>
              </Link>
            </Text>
          </Column>
        )}
      </Column>
    </Column>
  );
};

export default SignIn;
